/**
 * The first screen of "Create a report": Start from scratch, Import, then every template. Each template card
 * has an info button that opens the whole template in miniature, page by page.
 */
import { useEffect, useMemo, useState } from 'react';
import { FileUp, Info, PenLine, X } from 'lucide-react';
import { deriveTheme, type Theme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { SlideThumb } from '../SlideThumb';
import { REPORT_TEMPLATES, type ReportTemplate } from '../templates';

const titleOf = (s: Record<string, unknown>) => String(s.title || s.eyebrow || s.type || '').replace(/\*\*|\*|\+\+|~~/g, '');

export function TemplateGallery({ onScratch, onImport, onPick }: { onScratch: () => void; onImport: () => void; onPick: (t: ReportTemplate) => void }) {
  const theme = useMemo(() => deriveTheme({}), []);
  const [preview, setPreview] = useState<ReportTemplate | null>(null);
  const templates = REPORT_TEMPLATES.filter((t) => t.id !== 'blank');
  const tile = 'flex aspect-[16/9] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed text-center';
  return (
    <>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <button type="button" onClick={onScratch} className="group rounded-xl border border-[#e9d7fe] bg-[#fcf7ff] p-2 text-left transition hover:border-[#be43ff] hover:shadow-md">
          <div className={`${tile} border-[#d6bbfb] bg-white group-hover:border-[#be43ff]`}>
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#be43ff] text-white"><PenLine className="h-5 w-5" /></span>
          </div>
          <div className="mt-2 px-1 text-[13px] font-semibold text-[#101828]">Start from scratch</div>
          <div className="px-1 pb-1 text-[11px] leading-snug text-[#667085]">Write it, upload your material, or talk it through with Lykke. She builds it.</div>
        </button>
        <button type="button" onClick={onImport} className="group rounded-xl border border-[#eaecf0] p-2 text-left transition hover:border-[#be43ff] hover:shadow-md">
          <div className={`${tile} border-[#d0d5dd] bg-[#f9fafb] group-hover:border-[#be43ff]`}>
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#f4ebff] text-[#7a1fbf]"><FileUp className="h-5 w-5" /></span>
          </div>
          <div className="mt-2 px-1 text-[13px] font-semibold text-[#101828]">Import</div>
          <div className="px-1 pb-1 text-[11px] leading-snug text-[#667085]">PowerPoint, PDF or pictures. PowerPoint pages stay editable.</div>
        </button>
        {templates.map((t) => (
          <div key={t.id} className="group relative rounded-xl border border-[#eaecf0] p-2 transition hover:border-[#be43ff] hover:shadow-md">
            <button type="button" onClick={() => onPick(t)} className="block w-full text-left">
              <div className="relative">
                <div className="overflow-hidden rounded-lg border border-[#eaecf0]"><SlideThumb slide={t.slides[0]} index={0} theme={theme} title={t.name} /></div>
                {t.slides.length > 1 && (
                  <div className="absolute -bottom-2 left-3 right-3 flex gap-1">
                    {t.slides.slice(1, 4).map((s, i) => <div key={i} className="flex-1 overflow-hidden rounded border border-[#eaecf0] shadow-sm"><SlideThumb slide={s} index={i + 1} theme={theme} title={t.name} /></div>)}
                  </div>
                )}
              </div>
              <div className="mt-5 flex items-center justify-between px-1">
                <div className="text-[13px] font-semibold text-[#101828]">{t.name}</div>
                <div className="text-[11px] text-[#98a2b3]">{t.pages} page{t.pages === 1 ? '' : 's'}</div>
              </div>
              <div className="px-1 pb-1 text-[11px] leading-snug text-[#667085]">{t.description}</div>
            </button>
            {t.slides.length > 0 && (
              <button type="button" onClick={() => setPreview(t)} title="See every page" aria-label={`See every page of ${t.name}`}
                className="absolute right-3.5 top-3.5 grid h-7 w-7 place-items-center rounded-full bg-white/95 text-[#475467] shadow ring-1 ring-[#eaecf0] hover:text-[#7a1fbf] hover:ring-[#be43ff]">
                <Info className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      {preview && <TemplatePreview template={preview} theme={theme} onClose={() => setPreview(null)} onUse={() => { const t = preview; setPreview(null); onPick(t); }} />}
    </>
  );
}

/** Every page of a template in miniature: a short overview before choosing it. */
export function TemplatePreview({ template, theme, onClose, onUse }: { template: ReportTemplate; theme: Theme; onClose: () => void; onUse: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={`${template.name} — all pages`} className="fixed inset-0 z-[1200] flex items-center justify-center bg-[#101828]/50 p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[86vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start gap-3 border-b border-[#eaecf0] px-5 py-4">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-[#101828]">{template.name}</div>
            <div className="text-[12px] text-[#667085]">{template.slides.length} page{template.slides.length === 1 ? '' : 's'} · {template.description}</div>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {template.slides.map((s, i) => (
              <div key={i} className="overflow-hidden rounded-lg border border-[#eaecf0]">
                <SlideThumb slide={s} index={i} theme={theme} title={template.name} />
                <div className="flex gap-1.5 px-2 py-1 text-[11px] text-[#667085]"><span className="tabular-nums text-[#98a2b3]">{i + 1}</span><span className="truncate">{titleOf(s as Record<string, unknown>)}</span></div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[#eaecf0] px-5 py-3">
          <button onClick={onClose} className="rounded-lg border border-[#d0d5dd] px-3.5 py-2 text-[13px] font-medium text-[#344054] hover:bg-[#f9fafb]">Back</button>
          <button onClick={onUse} className="rounded-lg bg-[#be43ff] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#a835e0]">Use this template</button>
        </div>
      </div>
    </div>
  );
}
